/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  LabelValue,
  WbsLabelValueSearchResult,
  WbsLabelValueSearchInput
} from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { mappedLabelValues, LabelValueType } from '../LabelFacetDetails';
import { SearchBoxProps } from '../type';
import { scoredSuggestions } from '../scoredSuggestions';
import {
  searchClients,
  searchClientsByTerm
} from './hooks/useClientLabelSearch';

export const cleanSearchTerm = (value: string): string =>
  value.replace(/['",]+/g, '');

type SearchClientsHandler = (
  input: WbsLabelValueSearchInput
) => Promise<WbsLabelValueSearchResult[]>;

const matchOption = (
  searchClients: SearchClientsHandler,
  labelId: string
) => async (_: LabelValue[], term: string): Promise<LabelValueType | null> => {
  if (!term) return null;

  const clients = await searchClients({ query: cleanSearchTerm(term) });
  const mappedValues = mappedLabelValues(clients, labelId);
  const option = mappedValues && mappedValues.length > 0 && mappedValues[0];
  return option && option.displayText ? option : null;
};

const withCriteriaProvider = (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>,
  label: TimesheetLabel
) => (
  BaseComponent: React.FunctionComponent<SearchBoxProps>
): React.FC<SearchBoxProps> => (props: SearchBoxProps) => {
  const { searchCriteria: { criterions = {} } = {} } = props;
  const [selected, setSelected] = useState<LabelValueType[]>(
    (criterions[tag] as LabelValueType[]) || []
  );
  const apolloClient = useApolloClient();
  const $searchClients = searchClients({
    client: apolloClient
  });
  const searchClientsSuggestion = searchClientsByTerm({
    client: apolloClient
  });
  const value = useMemo(
    () => ({
      selected,
      setSelected,
      options: [],
      matchOption: matchOption($searchClients, label.id),
      searchClients: $searchClients,
      suggestions: scoredSuggestions(searchClientsSuggestion)
    }),
    [selected, $searchClients, searchClientsSuggestion]
  );

  return (
    <context.Provider value={value}>
      <BaseComponent {...props} />
    </context.Provider>
  );
};

type LabelCriteriaProviderContextType = {
  selected: LabelValueType[];
  setSelected: React.Dispatch<React.SetStateAction<LabelValueType[]>>;
  matchOption: (
    options: LabelValue[],
    term: string
  ) => Promise<LabelValueType | null>;
  searchClients: SearchClientsHandler;
};

const makeCriteriaProvider = (label: TimesheetLabel) => (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>
): ((
  BaseComponent: React.FunctionComponent<SearchBoxProps>
) => React.FC<SearchBoxProps>) => withCriteriaProvider(tag, context, label);

export default makeCriteriaProvider;
